import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import '../../styles/ConnectionAddonConfirmationModal.css';

const money = value => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
}).format(Number(value || 0));

const ConnectionAddonsCard = ({ addons, loading, changing, onChange }) => {
  const [drafts, setDrafts] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [password, setPassword] = useState('');
  const committedRef = useRef({});

  useEffect(() => {
    setDrafts(current => Object.fromEntries(addons.map(addon => {
      const previousCommitted = committedRef.current[addon.key];
      const hasLocalEdit = previousCommitted !== undefined && current[addon.key] !== previousCommitted;
      return [addon.key, hasLocalEdit ? current[addon.key] : addon.extra_quantity];
    })));
    committedRef.current = Object.fromEntries(addons.map(addon => [addon.key, addon.extra_quantity]));
  }, [addons]);

  const draftFor = addon => drafts[addon.key] ?? addon.extra_quantity;
  const setDraft = (addon, quantity) => setDrafts(current => ({
    ...current, [addon.key]: Math.max(0, quantity),
  }));
  const cancelConfirmation = () => {
    setDrafts(Object.fromEntries(addons.map(addon => [addon.key, addon.extra_quantity])));
    setConfirmation(null);
    setPassword('');
  };
  const confirmChange = async () => {
    const pending = confirmation;
    setConfirmation(null);
    if (pending) {
      for (const change of pending) await onChange(change.addon, change.quantity, password);
    }
    setPassword('');
  };

  const pendingChanges = addons
    .filter(addon => draftFor(addon) !== addon.extra_quantity)
    .map(addon => ({ addon, quantity: draftFor(addon) }));

  const confirmationTotals = (confirmation || []).reduce((totals, change) => ({
    current: totals.current + (change.addon.extra_quantity * change.addon.unit_price),
    next: totals.next + (change.quantity * change.addon.unit_price),
  }), { current: 0, next: 0 });

  return (
  <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '16px', background: '#fff' }}>
    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Conexões adicionais</h3>
        <small className="text-muted">Cada provedor inclui uma conexão. Os adicionais aumentam o limite contratado.</small>
      </div>
      <Badge bg="light" text="dark">Cobrança mensal</Badge>
    </div>
    <Alert variant="info">
      Remover uma conexão em <strong>Configurações &gt; Conexões</strong> não cancela a cobrança.
      Para reduzir o contratado, diminua a quantidade aqui. Se todas as vagas estiverem em uso,
      remova primeiro as conexões excedentes.
    </Alert>
    {loading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : (
      <div style={{ display: 'grid', gap: '12px' }}>
        {addons.map(addon => (
          <div key={addon.key} className="d-flex flex-wrap justify-content-between align-items-center gap-3" style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px' }}>
            <div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{addon.name}</div>
              <small className="text-muted">
                {addon.manual_quantity > 0 && <span>{addon.manual_quantity} extras sem cobrança · </span>}
                {addon.used_quantity} em uso de {addon.total_limit} disponíveis · {money(addon.unit_price / 100)} por adicional/mês
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button size="sm" variant="outline-secondary"
                disabled={!addon.is_available || draftFor(addon) === 0 || changing === addon.key}
                onClick={() => setDraft(addon, draftFor(addon) - 1)}>−</Button>
              <div className="text-center" style={{ minWidth: '88px' }}>
                <strong>{draftFor(addon)}</strong>
                <div className="text-muted" style={{ fontSize: '10px' }}>adicional(is)</div>
              </div>
              <Button size="sm" variant="primary" disabled={!addon.is_available || changing === addon.key}
                onClick={() => setDraft(addon, draftFor(addon) + 1)}>
                {changing === addon.key ? <Spinner animation="border" size="sm" /> : '+'}
              </Button>
            </div>
            {!addon.is_available && <div className="w-100 text-muted small">Adicional aguardando definição de preço pelo administrador.</div>}
          </div>
        ))}
        {pendingChanges.length > 0 && (
          <div className={'d-flex justify-content-end mt-2'}>
            <Button variant={'success'} disabled={!!changing} onClick={() => setConfirmation(pendingChanges)}>
              Revisar e confirmar alterações
            </Button>
          </div>
        )}
      </div>
    )}
    <Modal show={!!confirmation} onHide={cancelConfirmation} centered size={'lg'} className={'connection-addon-confirmation'}>
      <Modal.Header closeButton className={'connection-addon-confirmation__header'}>
        <div className={'connection-addon-confirmation__heading'}>
          <span className={'connection-addon-confirmation__icon'} aria-hidden={'true'}>✓</span>
          <div>
            <div className={'connection-addon-confirmation__eyebrow'}>Revisão segura</div>
            <Modal.Title>Confirmar alteração da assinatura</Modal.Title>
            <p>Confira os novos limites e valores antes de autorizar.</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className={'connection-addon-confirmation__body'}>
        {confirmation && (
          <>
            <div className={'connection-addon-confirmation__changes'}>
              {confirmation.map(change => (
                <div key={change.addon.key} className={'connection-addon-confirmation__change'}>
                  <div>
                    <span className={'connection-addon-confirmation__provider'}>Conexão adicional</span>
                    <strong>{change.addon.name}</strong>
                    <small>{money(change.addon.unit_price / 100)} por conexão/mês</small>
                  </div>
                  <div className={'connection-addon-confirmation__quantity'}>
                    <span>{change.addon.extra_quantity}</span><span className={'connection-addon-confirmation__arrow'}>→</span><strong>{change.quantity}</strong>
                  </div>
                </div>
              ))}
            </div>
            <div className={'connection-addon-confirmation__summary'}>
              <div><span>Valor mensal atual</span><strong>{money(confirmationTotals.current / 100)}</strong></div>
              <div><span>Novo valor mensal dos adicionais</span><strong>{money(confirmationTotals.next / 100)}</strong></div>
              <div className={'connection-addon-confirmation__difference'}><span>Alteração mensal</span><strong>{confirmationTotals.next - confirmationTotals.current >= 0 ? '+' : ''}{money((confirmationTotals.next - confirmationTotals.current) / 100)}</strong></div>
            </div>
            <div className={'connection-addon-confirmation__notice'}>
              <span aria-hidden={'true'}>i</span>
              <p>Aumento de limite gera cobrança proporcional até a próxima renovação e passa a integrar a mensalidade. Reduções respeitam o ciclo já pago. Fechar esta janela não altera sua assinatura.</p>
            </div>
            <Form.Group className={'connection-addon-confirmation__password'}>
              <Form.Label>Confirme com sua senha</Form.Label>
              <div className={'connection-addon-confirmation__input-wrap'}>
                <span aria-hidden={'true'}>●</span>
                <Form.Control type={'password'} value={password} autoComplete={'current-password'} onChange={event => setPassword(event.target.value)} placeholder={'Senha do Representante Legal'} />
              </div>
              <Form.Text className={'text-muted'}>
                Esta validação protege alterações que impactam sua cobrança.
              </Form.Text>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={'connection-addon-confirmation__footer'}>
        <Button variant={'light'} onClick={cancelConfirmation}>Voltar</Button>
        <Button className={'connection-addon-confirmation__confirm'} onClick={confirmChange} disabled={!confirmation || !password || !!changing}>
          {changing ? <Spinner animation={'border'} size={'sm'} /> : 'Confirmar alteração'}
        </Button>
      </Modal.Footer>
    </Modal>
  </section>
  );
};

export default ConnectionAddonsCard;
